/**
 * AuditAvatar — a 24×24 initials circle that sits on the timeline rail.
 *
 * - Fill: deterministic user-hash tint (so multi-user timelines are scannable).
 * - Ring: action semantic (create = green, update = blue, delete = red).
 * - Text: 1–2 initials derived from userName → email → userId fallback.
 * - Opaque enough to visually break the rail it sits on.
 */
import type { AuditAction } from '@armard/shared';

interface AuditAvatarProps {
  userName: string | null;
  userEmail: string | null;
  userId: string | null;
  action: AuditAction;
}

const ACTION_RING: Record<AuditAction, string> = {
  create: 'ring-green-400/50',
  update: 'ring-blue-400/50',
  delete: 'ring-red-400/50',
};

function hashHue(input: string): number {
  let h = 0;
  for (let i = 0; i < input.length; i++) {
    h = (h * 31 + input.charCodeAt(i)) | 0;
  }
  return Math.abs(h) % 360;
}

function initials(userName: string | null, userEmail: string | null, userId: string | null): string {
  if (userName) {
    const parts = userName.trim().split(/\s+/).filter(Boolean);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  }
  if (userEmail) {
    const local = userEmail.split('@')[0] ?? '';
    return local.slice(0, 2).toUpperCase() || '?';
  }
  if (userId) return userId.slice(0, 2).toUpperCase();
  return '?';
}

export function AuditAvatar({ userName, userEmail, userId, action }: AuditAvatarProps) {
  const hasUser = !!(userName || userEmail || userId);
  const seed = userId || userEmail || userName || 'anon';
  const hue = hashHue(seed);
  const label = initials(userName, userEmail, userId);

  const style = hasUser
    ? { backgroundColor: `hsl(${hue} 45% 30% / 0.9)`, color: `hsl(${hue} 70% 82%)` }
    : { backgroundColor: 'rgb(39 39 42 / 0.9)', color: 'rgb(255 255 255 / 0.6)' };

  const fullName = userName || userEmail || userId || 'Unbekannt';

  return (
    <span
      aria-hidden={false}
      title={fullName}
      style={style}
      className={`
        inline-flex h-6 w-6 items-center justify-center
        rounded-full text-[10px] font-semibold
        ring-2 ${ACTION_RING[action]}
        backdrop-blur-sm
      `}
    >
      {label}
    </span>
  );
}
